import React, { useMemo } from "react";
import { Box, HStack, Icon, Text, Tooltip, VStack } from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { ArtifactType, FileType, artifactTypeFromJSON } from "build-ts/common";
import { AppIcons } from "constants/icons";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";
import PandasTable from "modules/artifacts/dataframe/components/PandasTable";
import PandasTableFilters from "modules/artifacts/dataframe/components/PandasTableFilters";
import { AnalysisChartTab } from "modules/artifacts/dataframe/components/DataFrameAnalysis";
import {
  getPandasDfRowColCount,
  extractFormattedDataFromPandasDf,
  copyRawTableToClipboard,
  downloadRawTableAsExcel,
} from "modules/artifacts/dataframe/utils/pandasTableUtils";
import { DownloadMenu } from "modules/common/DownloadMenu";
import { ScheduledJobProto } from "build-ts/schedule";
import { HeaderWithStats } from "services/MetadataServices";

// ── Tab IDs ──────────────────────────────────────────────────────────────────

export const TAB = {
  outputs: "outputs",
  synthesis: "synthesis",
  inputs: "inputs",
} as const;

export type TabId = (typeof TAB)[keyof typeof TAB];

export function isValidTabId(v: string | undefined): v is TabId {
  return v === TAB.outputs || v === TAB.synthesis || v === TAB.inputs;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function pillLabel(artifact: ArtifactProto): string {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.FILE_ARTIFACT) return artifact.file_artifact?.header?.name ?? "File";
  return "Artifact";
}

function pillName(artifact: ArtifactProto): string {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.FILE_ARTIFACT) return artifact.file_artifact?.header?.name ?? "";
  return artifact.kg_dataframe?.header?.id ?? "";
}

function pillIcon(artifact: ArtifactProto): React.ElementType {
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.FILE_ARTIFACT) {
    const fileType = artifact.file_artifact?.file_type;
    if (fileType === FileType.CHART_SPEC_JSON) return AppIcons.chart;
    if (fileType === FileType.DOCX) return AppIcons.document;
    return AppIcons.dataframe; // CSV
  }
  return AppIcons.dataframe;
}

// ── Controlled ArtifactPillBar ────────────────────────────────────────────────

interface ArtifactPillBarProps {
  artifacts: ArtifactProto[];
  emptyLabel: string;
  /** Name of the currently selected pill — controlled by parent. */
  selectedPillName: string;
  /** Called when the user selects a different pill. */
  onPillChange: (name: string) => void;
  defaultTimeZone: string;
}

const ArtifactPillBar: React.FC<ArtifactPillBarProps> = ({
  artifacts,
  emptyLabel,
  selectedPillName,
  onPillChange,
  defaultTimeZone,
}) => {
  const activeArtifact = artifacts.find(a => pillName(a) === selectedPillName) ?? artifacts[0];
  const activeName = activeArtifact ? pillName(activeArtifact) : "";
  const activeType = activeArtifact ? artifactTypeFromJSON(activeArtifact.artifact_type) : null;

  const handlePillClick = (artifact: ArtifactProto) => {
    const name = pillName(artifact);
    if (name === activeName) return;
    onPillChange(name);
  };

  if (artifacts.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" h="100%" p={6}>
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          {emptyLabel}
        </Text>
      </Box>
    );
  }

  const renderFileContent = (artifact: ArtifactProto) => {
    const fa = artifact.file_artifact;
    if (!fa) return null;

    if (fa.file_type === FileType.CSV) {
      if (!fa.pandas_dataframe?.data_json) {
        return (
          <Box px={4} py={6} textAlign="center">
            <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
              No data available.
            </Text>
          </Box>
        );
      }
      return (
        <>
          {fa.pandas_dataframe.metadata && (
            <PandasTableFilters metadata={fa.pandas_dataframe.metadata} defaultTimeZone={defaultTimeZone} />
          )}
          <PandasTable df={fa.pandas_dataframe} />
        </>
      );
    }

    if (fa.file_type === FileType.CHART_SPEC_JSON) {
      if (!fa.chart_spec_json) {
        return (
          <Box px={4} py={6} textAlign="center">
            <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
              Chart data not available.
            </Text>
          </Box>
        );
      }
      return <AnalysisChartTab chartVegaSpecJson={fa.chart_spec_json} />;
    }

    // DOCX and other types
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          Preview not available for this file type.
        </Text>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" h="100%" minH={0}>
      {/* ── Pill bar ── */}
      <HStack
        spacing={1.5}
        px={2}
        py={1.5}
        borderBottom="1px solid"
        borderColor="gray.100"
        bg="white"
        flexWrap="wrap"
        flexShrink={0}
        position="sticky"
        overflowY="auto"
        top={0}
        zIndex={2}
        boxShadow="sm"
      >
        {artifacts.map(a => {
          const name = pillName(a);
          const isActive = name === activeName;
          const PillIcon = pillIcon(a);
          return (
            <Box
              key={name}
              as="button"
              type="button"
              display="inline-flex"
              alignItems="center"
              px={3}
              py={1}
              borderRadius="md"
              border={isActive ? "2px solid" : "1px solid"}
              borderColor={isActive ? "purple.600" : "gray.300"}
              bg="white"
              color={isActive ? "purple.700" : "gray.600"}
              fontSize="xs"
              fontWeight={isActive ? "700" : "600"}
              _hover={{ borderColor: "purple.400", color: "purple.600", bg: "purple.50" }}
              transition="all 0.15s"
              cursor="pointer"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handlePillClick(a);
              }}
            >
              <HStack as="span" spacing={1.5} align="center">
                <Icon as={PillIcon} boxSize={3} color="currentColor" flexShrink={0} aria-hidden />
                <Text as="span" fontSize="xs" lineHeight="1.2" mb={0} isTruncated>
                  {pillLabel(a)}
                </Text>
              </HStack>
            </Box>
          );
        })}
      </HStack>

      {/* ── Content area ── */}
      <Box flex={1} minH={0} display="flex" flexDir="column" overflow="hidden">
        {activeType === ArtifactType.FILE_ARTIFACT && activeArtifact && (
          <Box
            flex={1}
            minH={0}
            display="flex"
            flexDir="column"
            overflowY="auto"
            overflowX="hidden"
            onWheel={e => e.stopPropagation()}
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-gray-300)",
                borderRadius: "2px",
              },
            }}
          >
            {renderFileContent(activeArtifact)}
          </Box>
        )}

        {activeArtifact && activeType !== ArtifactType.FILE_ARTIFACT && (
          <Box display="flex" alignItems="center" justifyContent="center" h="100%" p={4}>
            <Text fontSize="sm" color="gray.400" mb={0}>
              Unable to display this artifact type inline.
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns the PandasDataframe for an artifact if it is tabular (KG dataframe or CSV file). */
function getArtifactPandasDf(artifact: ArtifactProto | null | undefined) {
  if (!artifact) return null;
  const type = artifactTypeFromJSON(artifact.artifact_type);
  if (type === ArtifactType.KG_DATAFRAME) return artifact.kg_dataframe?.pandas_dataframe ?? null;
  if (type === ArtifactType.FILE_ARTIFACT && artifact.file_artifact?.file_type === FileType.CSV) {
    return artifact.file_artifact.pandas_dataframe ?? null;
  }
  return null;
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: TAB.outputs, label: "Outputs", icon: AppIcons.results },
  { id: TAB.synthesis, label: "Synthesis", icon: AppIcons.findings },
  { id: TAB.inputs, label: "Inputs", icon: AppIcons.inputs },
];

// ── Pure component ────────────────────────────────────────────────────────────

export interface WorkflowRunCardProps {
  artifact: ArtifactProto;
  activeTab: TabId;
  selectedOutputPillName: string;
  selectedInputPillName: string;
  onTabChange: (tab: TabId) => void;
  onOutputPillChange: (name: string) => void;
  onInputPillChange: (name: string) => void;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
  defaultTimeZone?: string;
  /** When true, hides the artifact header and expand/chat actions (used in WorkflowRuns page). */
  hideHeader?: boolean;
  /** Analyst list, for analyst name lookup */
  analystList?: HeaderWithStats[];
  /** Scenario jobs, for owner ID lookup */
  scenarioJobs?: ScheduledJobProto[];
  /** Author display name resolved from the run's source_chat_id. */
  authorName?: string;
}

const WorkflowRunCard: React.FC<WorkflowRunCardProps> = ({
  artifact,
  activeTab,
  selectedOutputPillName,
  selectedInputPillName,
  onTabChange,
  onOutputPillChange,
  onInputPillChange,
  isExpanded,
  onExpandToggle,
  defaultTimeZone = "",
  hideHeader = false,
  analystList,
  scenarioJobs,
  authorName,
}) => {
  const run = artifact.workflow_run;
  const created = run?.header?.created_date;
  // Guard against empty/undefined timezone
  const safeTimeZone = defaultTimeZone || undefined;
  const timeLabel =
    created !== undefined && safeTimeZone
      ? epochConverterWithTimeZone(Number(created), "h:mm a, d MMM yyyy", safeTimeZone)
      : undefined;

  const findings = run?.findings?.trim() ?? "";
  const outputArtifacts = run?.output_artifacts ?? [];
  const inputArtifacts = run?.input_artifacts ?? [];

  const jobId = run?.job_id;
  const ownerId = scenarioJobs?.find(job => job.header?.id === jobId)?.header?.owner_id;
  const analystName = analystList?.find(analyst => analyst.id === ownerId)?.name;

  const activeOutputArtifact = useMemo(
    () => outputArtifacts.find(a => pillName(a) === selectedOutputPillName) ?? outputArtifacts[0] ?? null,
    [outputArtifacts, selectedOutputPillName]
  );
  const activeInputArtifact = useMemo(
    () => inputArtifacts.find(a => pillName(a) === selectedInputPillName) ?? inputArtifacts[0] ?? null,
    [inputArtifacts, selectedInputPillName]
  );

  const activeOutputPandasDf = useMemo(() => getArtifactPandasDf(activeOutputArtifact), [activeOutputArtifact]);
  const activeInputPandasDf = useMemo(() => getArtifactPandasDf(activeInputArtifact), [activeInputArtifact]);

  const { rows: outputRows, columns: outputCols } = useMemo(
    () => getPandasDfRowColCount(activeOutputPandasDf ?? undefined),
    [activeOutputPandasDf]
  );
  const { rows: inputRows, columns: inputCols } = useMemo(
    () => getPandasDfRowColCount(activeInputPandasDf ?? undefined),
    [activeInputPandasDf]
  );

  const actions = (
    <VStack spacing={0} align="flex-end">
      <HStack spacing={2} align="center">
        {/* Row / col count + download for active tab */}
        {activeTab === TAB.outputs && outputRows > 0 && outputCols > 0 && (
          <Text fontSize="xs" color="gray.700" mb={0}>
            {outputCols} {outputCols > 1 ? "cols" : "col"}, {outputRows} {outputRows > 1 ? "rows" : "row"}
          </Text>
        )}
        {activeTab === TAB.outputs && activeOutputPandasDf && (
          <DownloadMenu
            ariaLabel="Download"
            items={[
              {
                label: "Copy to clipboard",
                icon: "copy",
                onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(activeOutputPandasDf)),
              },
              {
                label: "Download Excel file",
                icon: "fileExcel",
                onClick: () =>
                  downloadRawTableAsExcel(
                    extractFormattedDataFromPandasDf(activeOutputPandasDf),
                    `${activeOutputPandasDf.name ?? "output"}.xlsx`
                  ),
              },
            ]}
          />
        )}

        {activeTab === TAB.inputs && inputRows > 0 && inputCols > 0 && (
          <Text fontSize="xs" color="gray.700" mb={0}>
            {inputCols} {inputCols > 1 ? "cols" : "col"}, {inputRows} {inputRows > 1 ? "rows" : "row"}
          </Text>
        )}
        {activeTab === TAB.inputs && activeInputPandasDf && (
          <DownloadMenu
            ariaLabel="Download"
            items={[
              {
                label: "Copy to clipboard",
                icon: "copy",
                onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(activeInputPandasDf)),
              },
              {
                label: "Download Excel file",
                icon: "fileExcel",
                onClick: () =>
                  downloadRawTableAsExcel(
                    extractFormattedDataFromPandasDf(activeInputPandasDf),
                    `${activeInputPandasDf.name ?? "input"}.xlsx`
                  ),
              },
            ]}
          />
        )}
      </HStack>
      <HStack spacing={2} align="center">
        {timeLabel && (
          <HStack spacing={1} align="center">
            <Icon as={AppIcons.clock} boxSize={3.5} color="gray.400" />
            <Text fontSize="xs" color="gray.500" fontWeight="500" mb={0} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {timeLabel}
            </Text>
          </HStack>
        )}
        {authorName && (
          <Tooltip label={authorName} placement="top" hasArrow openDelay={300}>
            <Box as="span" display="inline-flex" alignItems="center">
              <Icon as={AppIcons.user} boxSize={4} color="purple.700" cursor="pointer" />
            </Box>
          </Tooltip>
        )}
        {analystName && (
          <Tooltip label={analystName} placement="top" hasArrow openDelay={300}>
            <Box as="span" display="inline-flex" alignItems="center">
              <Icon as={AppIcons.analyst} boxSize={4} color="purple.700" cursor="pointer" />
            </Box>
          </Tooltip>
        )}
        {run?.source_chat_id && (
          <Tooltip label="View in chat" placement="top" hasArrow openDelay={300}>
            <Box as="span" display="inline-flex" alignItems="center">
              <Icon
                as={AppIcons.chatBubble}
                boxSize={4}
                color="purple.700"
                flexShrink={0}
                cursor="pointer"
                _hover={{ color: "purple.500" }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  window.open(`/chat/${run.source_chat_id}`, "_blank");
                }}
              />
            </Box>
          </Tooltip>
        )}
      </HStack>
    </VStack>
  );

  return (
    <Box w="100%" h="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.workflowRun}
        iconBg="white"
        iconColor="purple.600"
        name={run?.header?.description ?? run?.header?.name ?? "Workflow Run"}
        typeLabel="Workflow Run"
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={id => onTabChange(id as TabId)}
        actions={actions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
        hideHeader={hideHeader}
      />

      <Box flex={1} minH={0} display="flex" flexDir="column" overflow="hidden">
        {activeTab === TAB.outputs && (
          <ArtifactPillBar
            artifacts={outputArtifacts}
            emptyLabel="No outputs for this run."
            selectedPillName={selectedOutputPillName}
            onPillChange={onOutputPillChange}
            defaultTimeZone={defaultTimeZone}
          />
        )}

        {activeTab === TAB.synthesis && (
          <Box flex={1} minH={0} overflowY="auto" px={3} py={2} onWheel={e => e.stopPropagation()}>
            {findings ? (
              <MarkdownRenderer content={findings} fontSize="sm" />
            ) : (
              <VStack align="center" justify="center" h="100%" py={8}>
                <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                  No synthesis for this run.
                </Text>
              </VStack>
            )}
          </Box>
        )}

        {activeTab === TAB.inputs && (
          <ArtifactPillBar
            artifacts={inputArtifacts}
            emptyLabel="No inputs for this run."
            selectedPillName={selectedInputPillName}
            onPillChange={onInputPillChange}
            defaultTimeZone={defaultTimeZone}
          />
        )}
      </Box>
    </Box>
  );
};

export default WorkflowRunCard;
